import React, { createContext, useContext, useEffect, useState } from 'react';

export interface VersionInfo {
  app: {
    name: string;
    version: string;
    description: string;
  };
  build: {
    number: number;
    timestamp: string;
    environment: string;
  };
  git: {
    commit: string;
    branch: string;
    tag: string;
  };
}

interface VersionContextType {
  versionInfo: VersionInfo | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export const VersionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVersionInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/version.json');
      if (
        !response.ok ||
        !response.headers.get('content-type')?.includes('json')
      ) {
        // nginx SPA fallback returns index.html (text/html) for unknown routes
        // including /version.json. That's expected — version info is optional.
        return;
      }

      const data = await response.json();
      setVersionInfo(data);
    } catch {
      // Silent: version info is optional decoration, not a blocker.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersionInfo();
  }, []);

  return (
    <VersionContext.Provider
      value={{ versionInfo, loading, error, refresh: fetchVersionInfo }}
    >
      {children}
    </VersionContext.Provider>
  );
};

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersion must be used within VersionProvider');
  }
  return context;
};
